import { useCallback, useEffect, useRef } from 'react';
import {
  useCredentialsContext,
  type ParsedEndpoint,
  type TrackableHttpData,
} from '@terra/ui-design-system';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import type { EndpointReturnDto } from '@terra/shared-contracts-endpoints';
import { useCredentialsPreview } from '../../hooks/useCredentialsPreview';
import { terraProxyApiClient } from '../../clients/testerClient';
import { loadHistoryRequestForHitlDisplay } from '../../utils/loadHistoryRequestForHitlDisplay';
import { useHarById } from '../../hooks/api/useHarById';
import { useToolHarPresence } from '../../hooks/useToolHarPresence';
import { useCoPilotSessionExportDownload } from './session-export';
import { useTrackRequest } from '../../hooks/useTrackRequest';
import { useManualTrackWithCredentials } from '../../hooks/useManualTrackWithCredentials';
import { useBackgroundExecutionStore } from '../../store/backgroundExecutionStore';

export function useCopilotChatProviders({
  chatId,
  appTargetId,
}: {
  chatId: string;
  appTargetId: string | undefined;
}) {
  const { trackRequest } = useTrackRequest();
  const { requestManualTrack } = useManualTrackWithCredentials();
  const { fetchHarById } = useHarById();
  const { hasHarPresence } = useToolHarPresence(chatId);
  const downloadSessionExport = useCoPilotSessionExportDownload(chatId);

  const handleFetchHarData = useCallback(
    async ({
      threadId,
      toolCallId,
    }: {
      threadId: string;
      toolCallId: string;
    }): Promise<ToolExecutionHar[]> => {
      try {
        const response = await terraProxyApiClient.getToolHarsByToolCallId({
          threadId,
          toolCallId,
        });

        return response?.hars || [];
      } catch (error) {
        console.error('[ChatInstance] Error fetching HAR data:', error);

        return [];
      }
    },
    [],
  );

  const handleFetchHistoryRequestForHitlDisplay = useCallback(
    async (requestId: string) => {
      return loadHistoryRequestForHitlDisplay({ requestId });
    },
    [],
  );

  const handleFetchEndpoints = useCallback(async (): Promise<
    ParsedEndpoint[]
  > => {
    if (!appTargetId) return [];

    try {
      const result =
        await terraProxyApiClient.getEndpointsByApplication(appTargetId);

      return (result?.endpoints ?? []).map((ep: EndpointReturnDto) => ({
        endpointId: ep.id,
        endpointUrl: ep.url ?? undefined,
        path: ep.path ?? undefined,
        method: ep.request?.method ?? undefined,
        endpointDescription: ep.description ?? undefined,
      }));
    } catch (error) {
      console.error('[ChatInstance] Error fetching endpoints:', error);

      return [];
    }
  }, [appTargetId]);

  const { setCredentialsPreview } = useCredentialsContext();
  const { credentialsPreview } = useCredentialsPreview({
    applicationId: appTargetId,
  });

  const prevCredentialsPreviewRef = useRef<
    typeof credentialsPreview | undefined
  >(undefined);

  useEffect(() => {
    const prev = prevCredentialsPreviewRef.current;
    const hasChanged =
      !prev ||
      prev.length !== credentialsPreview.length ||
      prev.some((item, index) => item?.id !== credentialsPreview[index]?.id);

    if (hasChanged) {
      prevCredentialsPreviewRef.current = credentialsPreview;
      setCredentialsPreview(credentialsPreview);
    }
  }, [credentialsPreview, setCredentialsPreview]);

  const handleTrackHttpRequest = useCallback(
    async (data: TrackableHttpData) => {
      await requestManualTrack(async ({ credentialId }) => {
        const result = await trackRequest({
          request: {
            method: data.method,
            url: data.url,
            headers: data.headers,
            body: data.body,
            timestamp: Date.now(),
          },
          response: data.statusCode
            ? {
                statusCode: data.statusCode,
                statusMessage: data.statusText || '',
                headers: {},
                body: '',
              }
            : undefined,
          credentialId,
        });

        return result.success;
      });
    },
    [trackRequest, requestManualTrack],
  );

  const pendingRevisionForLookup = useBackgroundExecutionStore(
    (state) => state.pendingRevision,
  );

  const getBackgroundExecutionByToolCallId = useCallback(
    (toolCallId: string) => {
      void pendingRevisionForLookup;
      const pending = useBackgroundExecutionStore
        .getState()
        .getPendingExecution(toolCallId);

      if (!pending) {
        return undefined;
      }

      return {
        threadId: pending.agentThreadId,
        isRunning:
          pending.kind === 'subagent' &&
          !pending.waitingInput &&
          !pending.terminalStatus,
        terminalStatus: pending.terminalStatus,
      };
    },
    [pendingRevisionForLookup],
  );

  return {
    fetchHarById,
    hasHarPresence,
    downloadSessionExport,
    handleFetchHarData,
    handleFetchHistoryRequestForHitlDisplay,
    handleFetchEndpoints,
    handleTrackHttpRequest,
    getBackgroundExecutionByToolCallId,
  };
}
